import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MyApiClient from '../../../../../api-client/MyApiClient';
import CreaEventoModal from './CreaEventoModal';
import ModificaEventoModal from './ModificaEventoModal';

const api = new MyApiClient();

const CFG = {
    impianto: { color: '#1565C0', label: 'Impianto',        azione: "Vai all'impianto" },
    lezione:  { color: '#FF9800', label: 'Lezione',         azione: 'Vai alla lezione' },
    corso:    { color: '#00A15D', label: 'Corso e squadra', azione: 'Vai al corso'     },
    generico: { color: '#7B1FA2', label: 'Evento generico', azione: 'Modifica evento'  },
};

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    const giorni = ['Dom','Lun','Mar','Mer','Gio','Ven','Sab'];
    const mesi   = ['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
    return `${giorni[d.getDay()]} ${d.getDate()} ${mesi[d.getMonth()]} ${d.getFullYear()}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function cleanName(v) {
    if (!v || typeof v !== 'string') return null;
    if (UUID_RE.test(v.trim())) return null;
    return v;
}

function formatTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// Build type-specific campi from full event detail
function buildCampi(type, d) {
    const orario = d.dataInizio && d.dataFine
        ? `${formatTime(d.dataInizio)} – ${formatTime(d.dataFine)}`
        : (d.oraInizio && d.oraFine ? `${d.oraInizio} – ${d.oraFine}` : '—');
    const data = formatDate(d.dataInizio);

    if (type === 'impianto') return [
        { label: 'Impianto',    value: d.impianto?.nome ?? d.titolo },
        { label: 'Campo',       value: d.campo ?? '—' },
        { label: 'Data',        value: data },
        { label: 'Slot orario', value: orario },
        { label: 'Tipologia',   value: d.tipologia ?? '—' },
        { label: 'Note',        value: d.note ?? '—' },
    ];

    if (type === 'lezione') return [
        { label: 'Tipo lezione',  value: d.tipoLezione ?? d.tipologia ?? '—' },
        { label: 'Nome lezione',  value: d.titolo },
        { label: 'Data evento',   value: data },
        { label: 'Slot orario',   value: orario },
        { label: 'Staff tecnico', value: cleanName(d.staffTecnico?.nome) ?? cleanName(d.staffTecnico) ?? '—' },
        { label: 'Note',          value: d.note ?? '—' },
    ];

    if (type === 'corso') return [
        { label: 'Corso/Squadra',   value: d.squadra?.nome ?? d.corso?.nome ?? d.titolo },
        { label: 'Impianto',        value: d.impianto?.nome ?? '—' },
        { label: 'Data evento',     value: data },
        { label: 'Slot orario',     value: orario },
        { label: 'Staff tecnico',   value: cleanName(d.staffTecnico?.nome) ?? cleanName(d.staffTecnico) ?? '—' },
    ];

    // generico
    return [
        { label: 'Nome',        value: d.titolo },
        { label: 'Data',        value: data },
        { label: 'Orario',      value: orario },
        { label: 'Tipologia',   value: d.tipologia ?? '—' },
        { label: 'Luogo',       value: d.luogo ?? '—' },
        { label: 'Descrizione', value: d.descrizione ?? '—' },
    ];
}

function CampoRow({ label, value }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: '#aaa', fontWeight: 500, minWidth: 110, flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 13, color: '#222', fontWeight: 500 }}>{value}</span>
        </div>
    );
}

function PartRow({ p }) {
    const displayName = cleanName(p.nome) ?? '—';
    const initial    = displayName && displayName !== '—' ? displayName.charAt(0).toUpperCase() : '?';
    const avatarColor = p.avatarColore || '#aaa';

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {initial}
            </div>
            <span style={{ flex: 1, fontSize: 13, color: '#333', fontWeight: 500 }}>{displayName}</span>
            <span
                style={{
                    fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '3px 10px',
                    cursor: 'default', border: 'none',
                    background: p.pagato ? '#00A15D' : 'rgba(255,94,75,0.09)',
                    color:      p.pagato ? '#fff'    : '#FF5E4B',
                    outline:    p.pagato ? 'none'    : '1px solid rgba(255,94,75,0.3)',
                    pointerEvents: 'none',
                }}
            >
                {p.pagato ? 'Già pagato' : 'Non pagato'}
            </span>
        </div>
    );
}

export default function DettaglioEventoModal({ event, onClose, token, onRefresh }) {
    const navigate = useNavigate();
    const type = event.type || 'generico';
    const cfg  = CFG[type] || CFG.generico;

    const [dettaglio,    setDettaglio]    = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [partecipanti, setPartecipanti] = useState([]);
    const [editing,      setEditing]      = useState(false);
    const [rescheduling, setRescheduling] = useState(false);

    useEffect(() => {
        if (!token || !event.id) { setLoading(false); return; }
        api.getCalendarioEventoById(token, event.id).then(res => {
            setLoading(false);
            if (!res?.error) {
                setDettaglio(res);
                setPartecipanti(res.partecipanti || []);
            }
        });
    }, [token, event.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTogglePagamento = (partId, newPagato) => {
        setPartecipanti(prev => prev.map(p => p.id === partId ? { ...p, pagato: newPagato } : p));
        onRefresh && onRefresh();
    };

    // Use full detail when available, fallback to the event prop (already transformed by apiToViewEvento)
    const d     = dettaglio || event;
    const campi = buildCampi(type, d);

    const handleAzione = () => {
        if (type === 'generico') {
            setEditing(true);
            return;
        }
        let url = null;
        if (type === 'impianto') {
            const id = d.impianto?.id;
            url = id ? `/struttura/booking/facilitiesedit/${id}` : '/struttura/booking/facilities';
        } else if (type === 'lezione') {
            const id = d.lezione?.id;
            url = id ? `/struttura/booking/lessonseditmultiple/${id}` : '/struttura/booking/lessons';
        } else if (type === 'corso') {
            const id = d.corso?.id;
            // La scheda del corso di oggi (pages/struttura/corsi), non piu' le vecchie booking/courses
            url = id ? `/struttura/corsi/${id}` : '/struttura/corsi';
        }
        if (url) navigate(url);
    };

    if (editing) {
        return (
            <CreaEventoModal
                token={token}
                eventoToEdit={d}
                onClose={() => setEditing(false)}
                onCreated={() => { setEditing(false); onRefresh && onRefresh(); onClose(); }}
            />
        );
    }

    if (rescheduling) {
        return (
            <ModificaEventoModal
                event={d}
                token={token}
                onClose={() => setRescheduling(false)}
                onUpdated={() => { setRescheduling(false); onRefresh && onRefresh(); onClose(); }}
            />
        );
    }

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, width: 440, maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 60px)', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', borderBottom: '1px solid #f0f0f0', minHeight: 48, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: cfg.color, borderBottom: `2.5px solid ${cfg.color}`, padding: '14px 10px 12px', whiteSpace: 'nowrap' }}>
                        {cfg.label}
                    </span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#aaa', padding: 4 }}>✕</button>
                </div>

                {loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: '#ccc', fontSize: 13 }}>
                        Caricamento...
                    </div>
                ) : (
                    <>
                        {/* Campi dettaglio */}
                        <div style={{ padding: '16px 20px 8px' }}>
                            {campi.map((c, i) => <CampoRow key={i} label={c.label} value={c.value} />)}
                        </div>

                        <div style={{ height: 1, background: '#f0f0f0', margin: '4px 20px 0' }} />

                        {/* Partecipanti */}
                        <div style={{ padding: '14px 20px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#222' }}>Partecipanti</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: cfg.color, color: '#fff', borderRadius: 20, padding: '1px 8px' }}>
                                    {partecipanti.length}
                                </span>
                            </div>
                            {partecipanti.length === 0
                                ? <div style={{ fontSize: 13, color: '#ccc', textAlign: 'center', padding: '12px 0' }}>Nessun partecipante</div>
                                : (
                                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {partecipanti.map(p => (
                                            <PartRow key={p.id} p={p} />
                                        ))}
                                    </div>
                                )
                            }
                        </div>

                        {/* Footer */}
                        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', flexShrink: 0 }}>
                            <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#fff', color: '#333', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                Chiudi
                            </button>
                            {(type === 'impianto' || type === 'lezione' || type === 'corso') && (
                                <button onClick={() => setRescheduling(true)} style={{ flex: 1, padding: '10px', background: '#fff', color: cfg.color, border: `1.5px solid ${cfg.color}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                    Modifica
                                </button>
                            )}
                            <button onClick={handleAzione} style={{ flex: 1, padding: '10px', background: '#FF5E4B', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                {cfg.azione}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
